import { useState, useEffect } from 'react';
import { fetchStats } from '../common/api';

const STATUS_LABEL = {
  pending: { text: 'Pending', cls: 'bg-yellow-100 text-yellow-700' },
  confirmed: { text: 'Confirmed', cls: 'bg-blue-100 text-blue-700' },
  cancelled: { text: 'Cancelled', cls: 'bg-red-100 text-red-600' },
};

function formatKRW(n) {
  return `₩${Number(n || 0).toLocaleString('ko-KR')}`;
}

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function Sales() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchStats();
      setData(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-400 text-sm pt-24">Loading...</div>;
  if (error) return (
    <div className="p-8">
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">⚠️ {error}</div>
    </div>
  );

  const { monthly = [], recent = [], summary = {} } = data;
  const maxAmount = Math.max(...monthly.map((m) => Number(m.amount)), 1);

  const avgPerBooking = summary.total_bookings > 0
    ? Math.round(summary.total_revenue / summary.total_bookings)
    : 0;

  const summaryCards = [
    { label: 'Total Revenue', value: formatKRW(summary.total_revenue), sub: 'All time' },
    { label: 'This Month', value: formatKRW(summary.this_month_revenue), sub: 'Current month' },
    { label: 'Total Bookings', value: `${summary.total_bookings || 0}`, sub: 'All time' },
    { label: 'Avg. per Booking', value: formatKRW(avgPerBooking), sub: 'Average payment' },
  ];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Sales Statistics</h1>
          <p className="text-gray-500 text-sm mt-1">Revenue overview</p>
        </div>
        <button
          onClick={load}
          className="px-4 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
        >
          ↻ Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-white rounded-xl shadow-sm p-5">
            <p className="text-xs text-gray-500 font-medium">{card.label}</p>
            <p className="text-xl font-bold text-gray-800 mt-1">{card.value}</p>
            <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Bar Chart */}
      {monthly.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-6">Monthly Revenue</h2>
          <div className="flex items-end gap-3 h-48 px-2">
            {monthly.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                <span className="text-xs text-gray-500 truncate w-full text-center">
                  {formatKRW(m.amount).replace('₩', '')}
                </span>
                <div
                  className="w-full bg-[#E8876A] rounded-t-md hover:bg-[#D96F52] transition-colors cursor-default"
                  style={{ height: `${(Number(m.amount) / maxAmount) * 160}px`, minHeight: 4 }}
                  title={`${m.month}: ${formatKRW(m.amount)} / ${m.bookings} bookings`}
                />
                <span className="text-xs text-gray-500 mt-1 truncate w-full text-center">{m.month}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 text-center text-gray-400 text-sm">
          No revenue data yet.
        </div>
      )}

      {/* Recent Bookings */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700">Recent Bookings</h2>
        </div>
        {recent.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">No recent bookings.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tour</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Person(s)</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recent.map((b) => {
                const status = STATUS_LABEL[b.status] ?? STATUS_LABEL.pending;
                return (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-400">#{b.id}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{b.email}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-800">{b.tour_title}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{formatDate(b.date)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{b.persons}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-800">{formatKRW(b.total)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.cls}`}>
                        {status.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}